import { unwrap } from "@sagot/api-client";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { engageKeys } from "../engage-keys";
import { CONTEXT_POLL_MS, chunkIds, type CustomerContextItem } from "./customer-context-model";

const inboxDetail = (ws: string, conversationId: string) => [...engageKeys.inbox(ws), "detail", conversationId] as const;

/**
 * Query keys. The card and the lookup log hang under the conversation's detail key, so the inbox page's
 * existing refresh of that detail on every stream event refreshes them too.
 */
export const customerContextKeys = {
  card: (ws: string, conversationId: string, endUserId: string) => [...inboxDetail(ws, conversationId), "customer-context", endUserId] as const,
  lookups: (ws: string, conversationId: string) => [...inboxDetail(ws, conversationId), "customer-lookups"] as const,
  batch: (ws: string, ids: readonly string[]) => ["ws", ws, "customer-context", "batch", ...ids] as const,
};

/** `GET /v1/customers/{id}/context`: refetched every few seconds so a change the operator pushed shows up without a reload. */
export function useCustomerContext(conversationId: string, endUserId: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: customerContextKeys.card(ws, conversationId, endUserId),
    queryFn: ({ signal }) => unwrap(api.GET("/v1/customers/{end_user_id}/context", { params: { path: { end_user_id: endUserId } }, signal })),
    refetchInterval: CONTEXT_POLL_MS,
    retry: false,
  });
}

/** `POST /v1/customers/{id}/reference/reveal`: the full reference, kept only in the caller's state (never in the query cache). */
export function useRevealReference(endUserId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/v1/customers/{end_user_id}/reference/reveal", { params: { path: { end_user_id: endUserId } } })),
    gcTime: 0,
  });
}

/** `GET /v1/conversations/{id}/customer-lookups`: the lookups made in this chat. */
export function useConversationLookups(conversationId: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: customerContextKeys.lookups(ws, conversationId),
    queryFn: ({ signal }) => unwrap(api.GET("/v1/conversations/{conversation_id}/customer-lookups", { params: { path: { conversation_id: conversationId } }, signal })),
    refetchInterval: CONTEXT_POLL_MS,
    retry: false,
  });
}

export interface ContextBatch {
  /** Contact id to values; a contact with no linked customer is absent. */
  items: ReadonlyMap<string, CustomerContextItem>;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * `GET /v1/customers/context?contact_ids=`: tier, status and fields for the contacts on screen, 100 at a time.
 * While Secure launch is off the server answers an empty set, so the list simply has no customer columns.
 */
export function useCustomerContextBatch(contactIds: readonly string[], enabled = true): ContextBatch {
  const api = useApi();
  const ws = useWorkspaceId();
  const chunks = chunkIds(contactIds);
  const results = useQueries({
    queries: chunks.map((ids) => ({
      queryKey: customerContextKeys.batch(ws, ids),
      queryFn: ({ signal }: { signal: AbortSignal }) => unwrap(api.GET("/v1/customers/context", { params: { query: { contact_ids: ids.join(",") } }, signal })),
      enabled,
      staleTime: CONTEXT_POLL_MS * 4,
      retry: false,
    })),
  });
  const items = new Map<string, CustomerContextItem>();
  for (const result of results) {
    for (const [contactId, item] of Object.entries(result.data?.items ?? {})) {
      if (item) items.set(contactId, item);
    }
  }
  return {
    items,
    isPending: enabled && results.some((result) => result.isPending),
    isError: results.some((result) => result.isError),
    refetch: () => { results.forEach((result) => void result.refetch()); },
  };
}
